import { useTranslation } from 'next-i18next';

import { usePublicCategories } from '@/module/public/hooks/usePublicCategories';
import { usePublicCategoryFilter } from '@/module/public/hooks/usePublicCategoryFilter';
import { usePublicMetaFilter } from '@/module/public/hooks/usePublicMetaFilter';
import {
  formatGradeLabel,
  formatSemesterLabel,
} from '@/module/public/util/schoolInfoLabel';
import { type MapTemplateType, MapTemplateTypeLocaleKey } from '@/types';

export type FilterType =
  | 'official'
  | 'subject'
  | 'grades'
  | 'semesters'
  | 'publisher'
  | 'mapTemplate'
  | 'questionSize'
  | 'schoolType';

export const FilterTypeKeys: Record<FilterType, FilterType> = {
  schoolType: 'schoolType',
  official: 'official',
  subject: 'subject',
  grades: 'grades',
  semesters: 'semesters',
  publisher: 'publisher',
  mapTemplate: 'mapTemplate',
  questionSize: 'questionSize',
};

export type FilterSelectedValue = {
  name: string;
  value: string;
  type: FilterType;
};

export const useFilterSelectedValues = () => {
  const { t } = useTranslation(['common']);

  const { subjectGroups, publishers } = usePublicCategories();

  const {
    selectedGrades,
    selectedSubject,
    selectedSemesters,
    isSubSubjectSelected,
    handleSelectedSubject,
    handleSelectedGrades,
    handleSelectedSemesters,
    removeAll: removeAllCategory,
  } = usePublicCategoryFilter();

  const {
    checkedOfficialQuiz,
    selectedPublishers,
    selectedMapTemplate,
    selectedQuestionSize,
    handleSelectedMapTemplateType,
    handleToggleIsPublicQuiz,
    handleSelectedPublishers,
    handleSelectedQuestionSize,
    removeAll: removeAllMeta,
  } = usePublicMetaFilter();

  const subjectValue: FilterSelectedValue = (() => {
    if (isSubSubjectSelected) {
      const mainSubjectGroupId = selectedSubject?.slice(0, 2);

      return {
        name: String(
          subjectGroups[mainSubjectGroupId]?.children[selectedSubject]?.name ??
            '',
        ),
        value: selectedSubject,
        type: 'subject',
      };
    }

    return {
      name: String(subjectGroups[selectedSubject]?.name ?? ''),
      value: selectedSubject,
      type: 'subject',
    };
  })();

  // 배열 순서로 위치를 나타냅니다. 순서 변경에 주의 해주세요
  const values: {
    name: string;
    type: FilterType;
    value: string;
  }[] = [
    {
      value: checkedOfficialQuiz ? 'true' : '',
      name: t('common.officialQuiz'),
      type: FilterTypeKeys.official,
    },
    ...selectedGrades.map(v => ({
      value: v,
      name: formatGradeLabel(t, v),
      type: 'grades' as FilterType,
    })),
    ...selectedSemesters.map(v => ({
      value: v,
      name: formatSemesterLabel(t, v),
      type: FilterTypeKeys.semesters,
    })),
    subjectValue,
    ...selectedPublishers.map(v => ({
      value: publishers.length > 0 ? v : '',
      name: publishers.find(p => p.value === v)?.label ?? '',
      type: FilterTypeKeys.subject,
    })),
    {
      value: selectedMapTemplate,
      name: t(MapTemplateTypeLocaleKey[selectedMapTemplate as MapTemplateType]),
      type: FilterTypeKeys.mapTemplate,
    },
    {
      value: selectedQuestionSize,
      name: t('public.questionSize') + ' ' + selectedQuestionSize,
      type: FilterTypeKeys.questionSize,
    },
  ].filter(v => v.value !== '');

  const handleRemove = (value: string, type: FilterType) => {
    switch (type) {
      case 'official': {
        handleToggleIsPublicQuiz(false);
        break;
      }
      case 'subject': {
        handleSelectedSubject('');
        break;
      }
      // 복수개를 선택하는 경우는 토글 방식이므로 그래도 value 를 보내주면된다
      case 'grades': {
        void handleSelectedGrades(value);
        break;
      }
      case 'semesters': {
        handleSelectedSemesters(value);
        break;
      }
      case 'publisher':
        handleSelectedPublishers('');
        break;
      case 'mapTemplate':
        handleSelectedMapTemplateType('');
        break;
      case 'questionSize':
        handleSelectedQuestionSize('');
        break;
      case 'schoolType':
        // schoolType 필터 제거는 별도 카테고리 reset 흐름에서 처리됨
        break;
    }
  };

  const removeAll = () => {
    removeAllMeta();
    removeAllCategory({
      schoolType: true,
    });
  };

  return {
    values: values,
    iSetFilters: values.length > 0,
    removeAll,
    remove: handleRemove,
  };
};
